import type { FC } from 'react';
import React, { useContext, useCallback, useRef, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useSessionData } from '@confluence/session-data';
import { SPAViewContext } from '@confluence/spa-view-context';
import {
	TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE,
	ExperienceAbort,
	ExperienceFailure,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import {
	useEndOfTrialModalExperiment,
	statsigCohorts,
} from '@confluence/end-of-trial-payment-modals';
import { useIsPremiumDirectBuyStandardExperimentExposed } from '@confluence/experiment-premium-trial-buy-standard';
import { markErrorAsHandled } from '@confluence/graphql';
import { isTooManyRequestsError, isUnauthorizedError } from '@confluence/error-boundary';

import { getLicenseConsumingUserCountFromQuery } from '../graphql/getLicenseConsumingUserCountFromQuery';
import { getTrialDaysLeftFromQuery } from '../graphql/getTrialDaysLeftFromQuery';
import { TrialDetailsQuery } from '../graphql/TrialDetailsQuery.graphql';
import type { TrialDetailsQuery as TrialDetailsQueryType } from '../graphql/__types__/TrialDetailsQuery';
import { useHasBillingInformation } from '../useHasBillingInformation';
import { useLocalTrialEndDatesFromQuery } from '../graphql/useLocalTrialEndDatesFromQuery';
import { useBillingUrls } from '../useBillingUrls';

import { TrialFlagsComponent } from './TrialFlagsComponent';

const DAYS_TO_SHOW_FLAGS = [1, 3];
const ONBOARDING_STATE_KEY_PREFIX = 'trial-components:';
const mapDismissStateKey = (daysLeft: number | null) => `dismissedTrial${daysLeft}DayFlag`;

export const TrialFlags: FC = () => {
	const { isLicensed, isLoggedIn, userId, edition } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const experienceTracker = useContext(ExperienceTrackerContext);
	const hasExperienceTrackerStartedRef = useRef<Boolean>(false);

	const { setOnboardingState } = useSetOnboardingState();

	const isTrialEdition =
		edition === ConfluenceEdition.STANDARD || edition === ConfluenceEdition.PREMIUM;

	const skipTrialDetailsQuery =
		!isLicensed || !isLoggedIn || !isSiteAdmin || !userId || !isTrialEdition;

	const {
		data: trialDetailsData,
		loading: trialLoading,
		error: trialError,
	} = useQuery<TrialDetailsQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		TrialDetailsQuery,
		{
			skip: skipTrialDetailsQuery,
		},
	);

	const licenseConsumingUserCount = getLicenseConsumingUserCountFromQuery(trialDetailsData);
	const formattedDate = useLocalTrialEndDatesFromQuery(trialDetailsData);
	const deactivationDate = formattedDate?.deactivationDate;
	const trialDaysLeft = getTrialDaysLeftFromQuery(trialDetailsData);

	const isInTrial = trialDaysLeft !== null && trialDaysLeft > 0;
	const isTimeToShow = isInTrial && DAYS_TO_SHOW_FLAGS.includes(trialDaysLeft);
	const dismissedFlagKey = mapDismissStateKey(trialDaysLeft);

	const skipCommerceQueries = !isInTrial || !isTimeToShow || deactivationDate === undefined;

	const {
		paymentFlowUrl,
		loading: urlLoading,
		error: urlErr,
	} = useBillingUrls(skipCommerceQueries);

	const {
		hasBillingInformation,
		loading: billingLoading,
		error: billingErr,
	} = useHasBillingInformation(skipCommerceQueries);

	const skipGetOnboardingStateQuery = skipCommerceQueries || hasBillingInformation;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateErr,
	} = useGetOnboardingState(
		ONBOARDING_STATE_KEY_PREFIX.concat(dismissedFlagKey),
		skipGetOnboardingStateQuery,
	);

	const data = deserializeState(onboardingStateData);
	const hasDismissedFlag = data[dismissedFlagKey];

	const onDismissFlag = useCallback(() => {
		if (!hasDismissedFlag) {
			void setOnboardingState({
				key: ONBOARDING_STATE_KEY_PREFIX.concat(dismissedFlagKey),
				value: 'true',
			});
		}
	}, [dismissedFlagKey, hasDismissedFlag, setOnboardingState]);

	const {
		error: endOfTrialModalError,
		shouldShowModal,
		epMessageReady,
		cohort,
		fireExposureEvents,
		fireCloseModalEvents,
	} = useEndOfTrialModalExperiment();

	useEffect(() => {
		if (
			!endOfTrialModalError &&
			shouldShowModal &&
			cohort === statsigCohorts.CONTROL &&
			epMessageReady
		) {
			void fireExposureEvents();
			void fireCloseModalEvents();
		}
	}, [
		endOfTrialModalError,
		shouldShowModal,
		cohort,
		fireExposureEvents,
		fireCloseModalEvents,
		epMessageReady,
	]);

	// Suppress TrialFlags if the user was exposed to the Recommend Standard During Premium EOT treatment.
	const { isExposed: isPremiumDirectBuyStandardExperimentExposed } =
		useIsPremiumDirectBuyStandardExperimentExposed(!isTimeToShow);

	if (cohort === statsigCohorts.TREATMENT || isPremiumDirectBuyStandardExperimentExposed) {
		return null;
	}

	const canTrackExperience =
		!skipTrialDetailsQuery && !skipCommerceQueries && !skipGetOnboardingStateQuery;

	if (!hasExperienceTrackerStartedRef.current && canTrackExperience) {
		experienceTracker.start({
			name: TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE,
		});
		hasExperienceTrackerStartedRef.current = true;
	}

	const loading = trialLoading || urlLoading || billingLoading || onboardingStateLoading;

	// `TrialDetailsQuery` can throw Permission exception if initiater doesn't have sufficient permission
	// Permission error in this case is expected and FE should handle it gracefully - "markErrorAsHandled" and abort the experience instead of reporting as failures.
	if (trialError && isUnauthorizedError(trialError)) {
		markErrorAsHandled(trialError);
		return (
			<ExperienceAbort
				name={TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE}
				reason="Aborted due to insufficient permission"
			/>
		);
	}

	// `useHasBillingInformation` can cause a rate limiting error
	if (billingErr && isTooManyRequestsError(billingErr)) {
		markErrorAsHandled(billingErr);
		return (
			<ExperienceAbort
				name={TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE}
				reason="Aborted due to rate limit exceeded error"
			/>
		);
	}

	const error = trialError || urlErr || billingErr || onboardingStateErr;
	if (error) {
		return (
			<ExperienceFailure
				name={TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE}
				error={error}
				attributes={{
					trialErr: trialError?.message,
					urlErr: urlErr?.message,
					billingErr: billingErr?.message,
					onboardingStateErr: onboardingStateErr?.message,
				}}
			/>
		);
	}

	if (loading) return null;
	if (
		skipGetOnboardingStateQuery ||
		!onboardingStateData ||
		!DAYS_TO_SHOW_FLAGS.includes(trialDaysLeft) ||
		hasDismissedFlag ||
		!isTrialEdition
	) {
		experienceTracker.abort({
			name: TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE,
			reason: 'Aborted because a pre-requisite was not met',
		});
		return null;
	}

	return (
		<>
			<TrialFlagsComponent
				edition={edition}
				deactivationDateLong={deactivationDate}
				licenseConsumingUserCount={licenseConsumingUserCount}
				linkToAddBilling={paymentFlowUrl}
				trialDaysLeft={trialDaysLeft}
				onDismiss={onDismissFlag}
			/>
			<ExperienceSuccess name={TRIAL_COMPONENTS_FLAG_VIEW_EXPERIENCE} />
		</>
	);
};
